import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Space, Tooltip } from 'antd';
import { FileTextOutlined, CheckCircleOutlined, MailOutlined, MoreOutlined } from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { FormikProvider, setNestedObjectValues, useFormik } from 'formik';
import { hasPermissions } from 'config/roles/hasPermissions';
import useBreadcrumb from 'hooks/useBreadcrumb';
import useSession from 'hooks/useSession';
import { useInvoiceContext } from './InvoiceContextProvider';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import DotSeparator from 'components/new-ui/base/dot-separator/DotSeparator';
import Button from 'components/new-ui/base/button/Button';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import InvoiceStatus from '../components/InvoiceStatus';
import { StyledTabs } from 'components/new-ui/base/tabs/styles';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import Notification from 'components/ui/base/notification/Notification';
import useIndividuals from 'hooks/useIndividuals';
import { Individual } from 'store/individuals/individual';
import { InvoicesStatuses, UpdateInvoicePayload } from 'store/invoices/types';
import { downloadInvoicePdf, getPdfForDraftInvoice } from 'store/invoices/client';
import PopoverButtonsGroup from 'components/new-ui/composed/popover-buttons-group/PopoverButtonsGroup';
import { StyledPopoverButton } from 'components/new-ui/composed/popover-buttons-group/styles';
import {
    getActiveTab,
    initialInvoiceFieldValues,
    mapInvoiceToInitialValues,
    RE_SEND_INVOICE_STATUSES,
    toUtcMidnight,
} from '../utils';
import { invoiceDetailsValidationSchema } from './validationSchemas';
import { InvoiceDetailsTabs, InvoiceFieldValues } from '../types';
import MarkAsPaidModal from './components/MarkAsPaidModal';
import InvoiceCancelModal from './components/InvoiceCancelModal';
import SendToCustomerModal from './components/send-to-customer-modal/SendToCustomerModal';
import FilePreviewModal from 'components/new-ui/composed/modal/file-preview-modal/FilePreviewModal';
import useFilePreview from 'components/new-ui/composed/modal/file-preview-modal/useFilePreview';
import PartiallyPaidModal from './components/partially-paid/PartiallyPaidModal';
import EmailNotificationStatusTag from 'components/ui/composed/email-notification/EmailNotificationStatusTag';
import { useExportInvoice } from '../hooks/useExportInvoice';
import { InvoiceUtils } from 'utils/InvoiceUtils';
import DisputeInvoiceModal from './components/DisputeInvoiceModal';
import { isBeValidationError, resolveAxiosBlobError } from 'utils/common';
import { getErrorMessage } from 'utils/getErrorMessage';

type SubmitAction = 'save-as-draft' | 'save-and-send';
type PopoverType = 'export' | 'mark-as' | 'draft-actions' | null;
type ModalType =
    | 'mark-as-paid'
    | 'mark-as-partially-paid'
    | 'cancel-invoice'
    | 'send-to-customer'
    | 'dispute-invoice'
    | null;

const MIN_PAYABLE_REMAINDER = 0.01;

const BACKEND_TO_FORM_FIELD_MAP: Record<string, string> = {
    dueDate: 'invoiceDetails.dueDate',
    'customer.city': 'customerDetails.city',
    'customer.country': 'customerDetails.country',
    'customer.name': 'customerDetails.name',
    'customer.postCode': 'customerDetails.postCode',
    'customer.streetAndNumber': 'customerDetails.address',
};

interface Props {
    children: ReactNode;
}

const InvoiceDetails = ({ children }: Props) => {
    const { userInfo } = useSession();
    const location = useLocation();
    const navigate = useNavigate();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { invoiceId, invoice, updateInvoice, isLoading, declaration, missingBillingDetails } = useInvoiceContext();
    const { getIndividual } = useIndividuals();
    const { exportInvoice } = useExportInvoice();

    const [activeTabKey, setActiveTabKey] = useState<InvoiceDetailsTabs>(getActiveTab(location, invoiceId));
    const [creatorName, setCreatorName] = useState<string | undefined>(undefined);
    const [modal, setModal] = useState<ModalType>(null);
    const [popover, setPopover] = useState<PopoverType>(null);

    const { previewState, openPreviewWithBlob, closePreview, setGenerating } = useFilePreview();

    const submitActionRef = useRef<SubmitAction | null>(null);
    const canUpdateInvoices = hasPermissions(userInfo, 'invoices', 'UPDATE');
    const isReSendDisabled = Boolean(invoice?.sendToCustomerEmailStatus?.sendInProgress);

    const initialValues = useMemo<InvoiceFieldValues>(() => {
        if (!invoice) return initialInvoiceFieldValues;
        return mapInvoiceToInitialValues(invoice);
    }, [invoice]);

    const formik = useFormik<InvoiceFieldValues>({
        initialValues,
        validationSchema: invoiceDetailsValidationSchema,
        enableReinitialize: true,
        validateOnChange: false,
        onSubmit: async (values, helpers) => {
            if (!invoice?.id) {
                notifyError('Missing invoice data');
                helpers.setSubmitting(false);
                return;
            }

            const action = submitActionRef.current;

            try {
                showGlobalOverlay({
                    type: 'LoadingOverlay',
                    payload: { message: 'Saving invoice' },
                });

                const payload = buildUpdatePayload(values);
                await updateInvoice(invoice.id, payload);

                Notification({
                    messageTitle: 'Success',
                    description: 'Invoice saved as draft',
                    type: 'success',
                    placement: 'top',
                });

                if (action === 'save-and-send') {
                    setModal('send-to-customer');
                }
            } catch (err) {
                await applyBackendValidationError(
                    err,
                    helpers.setFieldError,
                    helpers.setFieldTouched,
                    'Could not save invoice. Please try again'
                );
            } finally {
                hideGlobalOverlay();
                submitActionRef.current = null;
                helpers.setSubmitting(false);
            }
        },
    });

    const closeModal = useCallback(() => {
        setModal(null);
    }, []);

    const closePopover = useCallback(() => {
        setPopover(null);
    }, []);

    const buildUpdatePayload = useCallback((values: InvoiceFieldValues): UpdateInvoicePayload => {
        const rawDueDate = values?.invoiceDetails?.dueDate;
        const dueDate = rawDueDate ? toUtcMidnight(rawDueDate) : undefined;

        return {
            customer: {
                city: values?.customerDetails?.city,
                country: values?.customerDetails?.country,
                name: values?.customerDetails?.name,
                postCode: values?.customerDetails?.postCode,
                streetAndNumber: values?.customerDetails?.address,
            },
            dueDate,
            taxLines: values.taxLines,
            additionalCharges: values.additionalCharges,
        };
    }, []);

    const notifyError = useCallback((description: string) => {
        Notification({ messageTitle: 'Error', description, type: 'error', placement: 'top' });
    }, []);

    const applyBackendValidationError = useCallback(
        async (
            err: unknown,
            setFieldError: (field: string, message: string) => void,
            setFieldTouched: (field: string, touched: boolean, shouldValidate?: boolean) => void,
            fallbackMessage: string
        ) => {
            const resolvedErr = await resolveAxiosBlobError(err);

            if (isBeValidationError(resolvedErr)) {
                resolvedErr.response!.data.subErrors.forEach(({ field, message }) => {
                    const fieldPath = BACKEND_TO_FORM_FIELD_MAP[field] ?? field;
                    setFieldError(fieldPath, message);
                    setFieldTouched(fieldPath, true, false);
                });
                notifyError('Please fix the highlighted fields and try again.');
                return;
            }

            notifyError(getErrorMessage(resolvedErr) || fallbackMessage);
        },
        [notifyError]
    );

    const handleCloseInvoice = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const handleExportDraftInvoices = useCallback(async () => {
        if (!invoice?.id || !invoice?.orderNumber) return notifyError('Missing invoice data');

        const payload = buildUpdatePayload(formik.values);
        return exportInvoice({
            type: 'draft-pdf',
            invoiceId: invoice.id,
            orderNumber: invoice.orderNumber,
            payload,
            onError: (err) =>
                applyBackendValidationError(
                    err,
                    formik.setFieldError,
                    formik.setFieldTouched,
                    'Could not export invoice PDF'
                ),
        });
    }, [
        applyBackendValidationError,
        buildUpdatePayload,
        formik.values,
        formik.setFieldError,
        formik.setFieldTouched,
        invoice?.id,
        invoice?.orderNumber,
        notifyError,
        exportInvoice,
    ]);

    const handlePreviewDraftInvoice = useCallback(async () => {
        if (!invoice?.id) return notifyError('Missing invoice data');

        try {
            setGenerating(true);
            const payload = buildUpdatePayload(formik.values);
            const blob = await getPdfForDraftInvoice(invoice.id, payload);
            openPreviewWithBlob(blob);
        } catch (err) {
            await applyBackendValidationError(
                err,
                formik.setFieldError,
                formik.setFieldTouched,
                'Could not generate invoice preview'
            );
            setGenerating(false);
        }
    }, [
        applyBackendValidationError,
        buildUpdatePayload,
        formik.values,
        formik.setFieldError,
        formik.setFieldTouched,
        invoice?.id,
        notifyError,
        openPreviewWithBlob,
        setGenerating,
    ]);

    const handleExportCSVDraftInvoice = useCallback(async () => {
        if (!invoice?.id || !invoice?.orderNumber) return notifyError('Missing invoice data');

        const payload = buildUpdatePayload(formik.values);
        return exportInvoice({
            type: 'draft-csv',
            invoiceId: invoice.id,
            orderNumber: invoice.orderNumber,
            payload,
            onError: (err) =>
                applyBackendValidationError(
                    err,
                    formik.setFieldError,
                    formik.setFieldTouched,
                    'Could not export invoice CSV'
                ),
        });
    }, [
        applyBackendValidationError,
        buildUpdatePayload,
        formik.values,
        formik.setFieldError,
        formik.setFieldTouched,
        invoice?.id,
        invoice?.orderNumber,
        notifyError,
        exportInvoice,
    ]);

    const handleSubmit = useCallback(
        async (action: SubmitAction) => {
            submitActionRef.current = action;

            const errors = await formik.validateForm();
            if (Object.keys(errors).length > 0) {
                submitActionRef.current = null;
                formik.setTouched(setNestedObjectValues(errors, true));
                notifyError('Please fix the highlighted fields before submitting.');
                return;
            }

            formik.submitForm();
        },
        [formik, notifyError]
    );

    const handleViewInvoice = useCallback(async () => {
        if (!invoice?.id) return notifyError('Missing invoice data');
        try {
            setGenerating(true);

            const documentResponse = await downloadInvoicePdf(invoice.id);
            const response = await fetch(documentResponse.preSignedUrl);

            if (response?.status >= 400) {
                throw new Error('Could not get file from the server. Please try again later.');
            }

            const blob = await response.blob();
            openPreviewWithBlob(blob);
        } catch (err: any) {
            notifyError(err?.message || 'Could not generate the invoice PDF. Please try again later.');
            setGenerating(false);
        }
    }, [invoice?.id, notifyError, openPreviewWithBlob, setGenerating]);

    const handleExportInvoiceCSV = () => {
        if (invoice?.id && invoice?.orderNumber) {
            return exportInvoice({ type: 'csv', invoiceId: invoice.id, orderNumber: invoice.orderNumber });
        }
    };

    const handleExportInvoicePdf = () => {
        if (invoice?.id && invoice?.orderNumber) {
            return exportInvoice({ type: 'pdf', invoiceId: invoice.id, orderNumber: invoice.orderNumber });
        }
    };

    useEffect(() => {
        setBreadcrumbRoutes([
            { breadcrumbName: 'Home', path: '/home' },
            { breadcrumbName: 'Invoices', path: '../invoices' },
            { breadcrumbName: 'Invoice Details', path: '' },
        ]);
    }, [setBreadcrumbRoutes]);

    useEffect(() => {
        if (invoice?.createdBy) {
            getIndividual(invoice.createdBy)
                .then((ind: Individual) => setCreatorName(ind?.fullName || ind?.email))
                .catch(() => setCreatorName(undefined));
        }
    }, [getIndividual, invoice?.createdBy]);

    useEffect(() => {
        if (isLoading && !invoice) {
            showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Loading invoice details...' } });
        } else {
            hideGlobalOverlay();
        }
    }, [hideGlobalOverlay, isLoading, invoice, showGlobalOverlay]);

    const tabItems = useMemo(
        () => [
            { key: InvoiceDetailsTabs.OVERVIEW, label: 'Overview' },
            { key: InvoiceDetailsTabs.HISTORY, label: 'History' },
        ],
        []
    );

    const renderSidebarActions = () => {
        if (!invoice?.status) return null;
        if (!canUpdateInvoices) return <Button onClick={handleCloseInvoice}>Close</Button>;

        const remainingPayment = InvoiceUtils.getRemainingPayment(invoice.totalAmount, invoice.amountPaid);
        const canPartiallyPay = remainingPayment > MIN_PAYABLE_REMAINDER;

        switch (invoice.status) {
            case InvoicesStatuses.DRAFT:
                return (
                    <Space size={8}>
                        <PopoverButtonsGroup
                            open={popover === 'draft-actions'}
                            onOpenChange={(next) => setPopover(next ? 'draft-actions' : null)}
                            buttonEl={
                                <Button style={{ padding: 8, color: 'var(--brand-6)', borderColor: 'var(--brand-6)' }}>
                                    <MoreOutlined style={{ display: 'flex', alignItems: 'center' }} />
                                </Button>
                            }
                        >
                            <StyledPopoverButton
                                onClick={() => {
                                    handleSubmit('save-as-draft');
                                    closePopover();
                                }}
                            >
                                Save as Draft
                            </StyledPopoverButton>
                            <StyledPopoverButton
                                onClick={() => {
                                    setModal('cancel-invoice');
                                    closePopover();
                                }}
                            >
                                Cancel Invoice
                            </StyledPopoverButton>
                        </PopoverButtonsGroup>
                        <Tooltip
                            title={
                                missingBillingDetails ? 'Add billing details before sending this invoice' : undefined
                            }
                        >
                            <span>
                                <Button
                                    loading={isLoading}
                                    onClick={() => handleSubmit('save-and-send')}
                                    icon={<MailOutlined />}
                                    type="primary"
                                    disabled={missingBillingDetails}
                                >
                                    Save and Send
                                </Button>
                            </span>
                        </Tooltip>
                    </Space>
                );

            case InvoicesStatuses.OVERDUE:
            case InvoicesStatuses.AWAITING_PAYMENT:
            case InvoicesStatuses.PARTIALLY_PAID:
            case InvoicesStatuses.DISPUTED:
                const canDispute =
                    invoice.status === InvoicesStatuses.AWAITING_PAYMENT ||
                    invoice.status === InvoicesStatuses.PARTIALLY_PAID;

                return (
                    <Space size={8}>
                        <Button onClick={handleCloseInvoice}>Close</Button>
                        <PopoverButtonsGroup
                            open={popover === 'mark-as'}
                            onOpenChange={(next) => setPopover(next ? 'mark-as' : null)}
                            buttonEl={
                                <Button type="primary" icon={<CheckCircleOutlined />}>
                                    Mark As
                                </Button>
                            }
                        >
                            <StyledPopoverButton
                                onClick={() => {
                                    setModal('mark-as-paid');
                                    closePopover();
                                }}
                            >
                                Paid
                            </StyledPopoverButton>
                            <Tooltip
                                title={
                                    canPartiallyPay
                                        ? undefined
                                        : 'The remaining balance is too small to be partially paid'
                                }
                            >
                                <span>
                                    <StyledPopoverButton
                                        disabled={!canPartiallyPay}
                                        onClick={() => {
                                            setModal('mark-as-partially-paid');
                                            closePopover();
                                        }}
                                    >
                                        Partially Paid
                                    </StyledPopoverButton>
                                </span>
                            </Tooltip>
                            {canDispute && (
                                <StyledPopoverButton
                                    onClick={() => {
                                        setModal('dispute-invoice');
                                        closePopover();
                                    }}
                                >
                                    Disputed
                                </StyledPopoverButton>
                            )}
                            <StyledPopoverButton
                                onClick={() => {
                                    setModal('cancel-invoice');
                                    closePopover();
                                }}
                            >
                                Cancelled
                            </StyledPopoverButton>
                        </PopoverButtonsGroup>
                    </Space>
                );

            default:
                return <Button onClick={handleCloseInvoice}>Close</Button>;
        }
    };

    const renderSecondaryActions = () => {
        if (!canUpdateInvoices || !invoice?.status) return null;

        if (invoice.status === InvoicesStatuses.DRAFT) {
            return (
                <Space size={8}>
                    <Button loading={previewState.generating} onClick={handlePreviewDraftInvoice}>
                        Preview
                    </Button>
                    <PopoverButtonsGroup
                        open={popover === 'export'}
                        onOpenChange={(next) => setPopover(next ? 'export' : null)}
                        buttonEl={<Button>Export</Button>}
                    >
                        <StyledPopoverButton
                            onClick={() => {
                                handleExportCSVDraftInvoice();
                                closePopover();
                            }}
                        >
                            Export CSV
                        </StyledPopoverButton>
                        <StyledPopoverButton
                            onClick={() => {
                                handleExportDraftInvoices();
                                closePopover();
                            }}
                        >
                            Export PDF
                        </StyledPopoverButton>
                    </PopoverButtonsGroup>
                </Space>
            );
        }

        return (
            <Space size={8}>
                <Button onClick={handleViewInvoice}>View</Button>
                <PopoverButtonsGroup
                    open={popover === 'export'}
                    onOpenChange={(next) => setPopover(next ? 'export' : null)}
                    buttonEl={<Button>Export</Button>}
                >
                    <StyledPopoverButton
                        onClick={() => {
                            handleExportInvoiceCSV();
                            closePopover();
                        }}
                    >
                        Export CSV
                    </StyledPopoverButton>
                    <StyledPopoverButton
                        onClick={() => {
                            handleExportInvoicePdf();
                            closePopover();
                        }}
                    >
                        Export PDF
                    </StyledPopoverButton>
                </PopoverButtonsGroup>

                {RE_SEND_INVOICE_STATUSES.includes(invoice.status) && (
                    <Tooltip title={isReSendDisabled ? 'An email is already being sent for this invoice.' : undefined}>
                        <span>
                            <Button disabled={isReSendDisabled} onClick={() => setModal('send-to-customer')}>
                                Re-send
                            </Button>
                        </span>
                    </Tooltip>
                )}
            </Space>
        );
    };

    const creationDate = moment(invoice?.createdAt).format('DD/MM/YYYY');
    const dateAndCreator = creatorName ? `Created By: ${creatorName}, ${creationDate}` : `Created: ${creationDate}`;

    if (!invoice) {
        return null;
    }

    if (!invoice) {
        return (
            <StyledPageWrapper>
                <PageHeader title="Invoice Details" icon={<FileTextOutlined />} />
                <div>Invoice not found.</div>
            </StyledPageWrapper>
        );
    }

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <StyledPageWrapper>
                    <PageHeader
                        title="Invoice Details"
                        icon={null}
                        showBackButton
                        onBackButtonClick={() => navigate(-1)}
                        additionalContent={
                            <Space size={8} style={{ marginLeft: 16, alignSelf: 'flex-end' }}>
                                <InvoiceStatus status={invoice.status} />
                                <DotSeparator />
                                <span>{dateAndCreator}</span>

                                {declaration?.mrn && (
                                    <>
                                        <DotSeparator />
                                        <Link to={`/declarations/${declaration.id}`}>{declaration.mrn}</Link>
                                    </>
                                )}

                                {invoice.sendToCustomerEmailStatus && (
                                    <>
                                        <DotSeparator />
                                        <EmailNotificationStatusTag
                                            overallStatus={invoice.sendToCustomerEmailStatus.overallStatus}
                                            providerStatus={invoice.sendToCustomerEmailStatus.overallProviderEventType}
                                        />
                                    </>
                                )}
                            </Space>
                        }
                        sidebar={renderSidebarActions()}
                    />
                    <StyledTabs
                        style={{ marginTop: 24, marginBottom: 12 }}
                        activeKey={activeTabKey}
                        onChange={(key) => {
                            const activeKey = key as InvoiceDetailsTabs;
                            if (activeKey === InvoiceDetailsTabs.OVERVIEW) {
                                navigate(`/invoices/${invoiceId}`, {
                                    replace: true,
                                });
                            } else {
                                navigate(`/invoices/${invoiceId}/history`, {
                                    replace: true,
                                });
                            }
                            setActiveTabKey(activeKey);
                        }}
                        items={tabItems}
                        tabBarExtraContent={renderSecondaryActions()}
                    />

                    {children}
                </StyledPageWrapper>
            </form>

            <MarkAsPaidModal invoiceId={invoice.id} open={modal === 'mark-as-paid'} onClose={closeModal} />
            <PartiallyPaidModal invoiceId={invoice.id} open={modal === 'mark-as-partially-paid'} onClose={closeModal} />
            <InvoiceCancelModal invoiceId={invoice.id} open={modal === 'cancel-invoice'} onClose={closeModal} />
            <DisputeInvoiceModal invoiceId={invoice.id} open={modal === 'dispute-invoice'} onClose={closeModal} />
            <SendToCustomerModal open={modal === 'send-to-customer'} onClose={closeModal} />

            <FilePreviewModal
                title="PDF Preview"
                open={previewState.open}
                url={previewState.url ?? ''}
                contentType={previewState.contentType}
                onClose={closePreview}
            />
        </FormikProvider>
    );
};

export default InvoiceDetails;
